import { useCallback, useEffect, useRef, useState } from 'react';
import { C } from '../../../lib/theme';
import { styles } from '../../../lib/styles';
import { fmtDateTime, genId } from '../../../lib/util';
import { usePrefs, setPrefs } from '../../../lib/prefs';
import { formatOrgType, KYC_ITEMS } from '../../../domain/constants';
import { kycItemProgress, kycMinimumsApply, progressText, shortfallReason } from '../../../domain/kyc-progress';
import { compareLogoDomain, httpUrl } from '../../../lib/domain-match';
import type { Case } from '../../../domain/initial-cases';
import { personName } from '../../../auth/actorNames';
import { getCurrentUser } from '../../../auth/useUser';
import { useAppData } from '../../../state/contexts/AppDataContext';
import { useUIState } from '../../../state/contexts/UIStateContext';
import { FinancialReferencesPanel } from './FinancialReferencesPanel';
import { Dialog } from '../../../components/Dialog';
import { EvidenceAttachments, useEvidenceFiles } from '../../../components/EvidenceAttachments';
import { attachedTo } from '../../../api/evidenceFiles';

// #513 — the fields the onboarding-data card below actually renders, and so the
// only ones that can decide whether it is worth rendering. Kept beside the card
// deliberately: the condition and the markup have to name the same fields, and
// a field in one but not the other is the drift this list exists to make
// visible.
//
// The card USED to key on `onboarding.status !== 'DRAFT'`, a guard dating from
// 2026-05-05 when DRAFT meant "the OA has not filled this in yet, so there is
// nothing worth showing". Under R11 intake, DRAFT means "the customer submitted
// it and staff promoted it, but no analyst has worked it yet" — the opposite —
// so every intake-sourced case hid the identity the analyst was on the tab to
// verify, and hid it silently.
const CARD_FIELDS = [
  'legalName', 'fein', 'website', 'orgType', 'jurisdiction', 'sicCode', 'sicDescription',
  'authorizerName', 'authorizerEmail', 'address', 'city', 'state', 'zip', 'callLogoUrl',
] as const;

// Emptiness is judged on the VALUES, not on the object. createEmptyOnboarding()
// seeds every one of these keys with an empty string, so `data &&
// Object.keys(data).length` is true for a form nobody has touched — and a
// staff-created case would render a card of bare labels.
function hasOnboardingData(data: Record<string, unknown> | undefined | null): boolean {
  if (!data) return false;
  return CARD_FIELDS.some((k) => String(data[k] ?? '').trim() !== '');
}

// #696 — the id the collapse button's aria-controls points at, on whichever of
// the two bodies is rendered. One card per page, so a constant is enough.
const ONBOARDING_BODY_ID = 'kyc-onboarding-data-body';

// The collapsed line: the four things an analyst reads off a Secretary of
// State record. Blank fields are dropped rather than rendered as empty
// separators — an intake case carries no SIC and often no website, and a line
// reading "Acme Corp ·  · DE · " is worse than a shorter one.
function summaryLine(data: Record<string, unknown> | undefined | null): string {
  const s = (k: string) => String(data?.[k] ?? '').trim();
  const addr = [s('address'), s('city'), [s('state'), s('zip')].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
  const line = [s('legalName'), s('fein'), s('jurisdiction'), addr].filter(Boolean).join(' · ');
  // The card appears when ANY of its fourteen fields carries a value, and the
  // summary shows four of them, so a case carrying only a website or a SIC
  // code collapses to nothing at all. An empty strip with an empty tooltip
  // reads as a broken card rather than as a card with nothing to summarise.
  return line || 'No summary fields — expand for the full card';
}

// #696 — whether the card is collapsed is a device preference, stored with the
// rest of them in lib/prefs.ts. That is the one file in this bundle permitted
// to write web storage — the CI gate bans the bare identifiers outright,
// whatever the value, and exempts that file BY NAME so a second writer has to
// go and edit the gate — so routing through it is what makes the choice
// survive a reload rather than only a remount. Nothing about it is per case:
// an analyst who wants the one-line summary wants it on the next case too.

export function KycTab({ c }: { c: Case }) {
  const { updateCase, showToast } = useAppData();
  const { kycExpanded, setKycExpanded } = useUIState();
  const user = getCurrentUser()!;
  // #776 — "Verified by" as a NAME. Evidence stores the recorder's Keycloak
  // sub, which this tab used to print raw; the API now projects
  // `verifierNames` (sub -> users.name) on every case read. The reader's own
  // sub resolves locally, for the record they just filed and whose name the
  // PATCH response has not brought back yet.
  const verifiedBy = (sub: string | null | undefined): string =>
    personName(sub, sub ? c.verifierNames?.[sub] : null, { self: { id: user.id, name: user.name } });

  // M-12: the Add Evidence form is controlled state (only one item expands
  // at a time, so one set of fields serves them all) with an inline error
  // for the required findings field, replacing the uncontrolled-$ reads
  // that silently swallowed the click.
  const [evSource, setEvSource] = useState('');
  const [evResult, setEvResult] = useState('clear');
  const [evSearch, setEvSearch] = useState('');
  const [evFindings, setEvFindings] = useState('');
  const [evError, setEvError] = useState<string | null>(null);
  // #774 — screenshots and PDFs. The form's files are STAGED until its record
  // is saved (the API attaches a file only to a record that exists), then
  // handed to that record's attachments as `queued`, which uploads them. The
  // link is held on the file, never in `c.kyc`, so the evidence minimums
  // (#772) count records exactly as before: a file is not a record.
  const { files: attached, refresh: refreshAttached } = useEvidenceFiles(c.id);
  const [evStaged, setEvStaged] = useState<File[]>([]);
  const [queued, setQueued] = useState<Record<string, File[]>>({});
  const takeQueued = useCallback((evidenceId: string) => {
    setQueued((q) => { const { [evidenceId]: _taken, ...rest } = q; return rest; });
  }, []);
  const addFormRef = useRef<HTMLDivElement>(null);

  // #696 — read from the preference store and written straight back to it.
  // usePrefs subscribes this component to the change, so there is no second
  // copy of the flag in component state to fall out of step with the stored
  // one. setPrefs swallows a storage failure: the card still collapses for
  // this session, only the remembering is lost.
  // #690 — the evidence record an admin has asked to delete, held while the
  // confirmation is on screen. The record is addressed by id (index only as a
  // fallback for rows predating genId('EVD')) and RE-RESOLVED against the
  // current props on confirm, not captured at click time: the dialog can sit
  // open across a re-render, and a stale object reference would filter nothing
  // out and PATCH the list back unchanged.
  const [confirmRemove, setConfirmRemove] = useState<
    { key: string; id: string | undefined; index: number } | null
  >(null);

  const { kycCardCollapsed: cardCollapsed } = usePrefs();
  const setCardCollapsed = (next: boolean) => { setPrefs({ kycCardCollapsed: next }); };
  useEffect(() => {
    setEvSource(''); setEvResult('clear'); setEvSearch(''); setEvFindings(''); setEvError(null); setEvStaged([]);
  }, [kycExpanded]);

  // #690, as #719 leaves it — deleting an evidence record, now open to every
  // staff role that can write the case. The server no longer gates on ROLE; it
  // still gates on TRUTH, comparing the evidence ids in the body against the
  // stored ones and answering 422 when a body carrying KYC_EVIDENCE_REMOVED
  // removes nothing, because `cases.kyc` is an opaque JSONB object and a
  // hand-crafted PATCH could otherwise file a deletion nobody made.
  //
  // ONE PATCH: the filtered list, the recomputed `verified`, and the audit row
  // travel together, the same shape #689 uses for the save.
  const removeEvidence = (key: string, target: { id: string | undefined; index: number }) => {
    const field = c.kyc[key] || { verified: false, evidence: [] };
    const list: any[] = field.evidence || [];
    const i = target.id ? list.findIndex((e: any) => e.id === target.id) : target.index;
    const removed = i >= 0 ? list[i] : undefined;
    // Already gone — another tab's PATCH landed first. Closing the dialog is
    // the whole response; PATCHing an unchanged list would write an audit row
    // claiming a deletion that this click did not perform.
    if (!removed) { setConfirmRemove(null); return; }
    const remaining = list.filter((_: any, j: number) => j !== i);
    // Auto-uncheck, the mirror of #689's auto-verify: an item left below its
    // evidence minimum (#772) cannot stand as verified. At or above it,
    // `verified` is the analyst's and is not touched in either direction. A
    // grandfathered case keeps the old rule: only an EMPTY item unchecks.
    const stillMet = kycMinimumsApply(c)
      ? kycItemProgress(KYC_ITEMS[key], { evidence: remaining }, c.financialRefs).met
      : remaining.length > 0;
    const verified = stillMet ? (field.verified ?? false) : false;
    updateCase(c.id, {
      kyc: { ...c.kyc, [key]: { ...field, evidence: remaining, verified } },
      action: {
        code: 'KYC_EVIDENCE_REMOVED',
        detail: {
          item: key,
          label: KYC_ITEMS[key]?.label ?? key,
          verified,
          // The record itself is about to stop existing, so the trail keeps a
          // copy of what it said rather than only that something was deleted.
          record: {
            id: removed.id ?? null,
            source: removed.source ?? null,
            result: removed.result ?? null,
            findings: removed.findings ?? null,
            verifiedBy: removed.verifiedBy ?? null,
            date: removed.date ?? null,
          },
        },
      },
    });
    setConfirmRemove(null);
  };

  // #769 — the call logo, beside the website it should be hosted on. Linked
  // only when it is an http(s) URL: the value is registrant-supplied, and any
  // other scheme (javascript: above all) is shown as text, never as an href.
  const logoText = String(c.onboarding?.data?.callLogoUrl ?? '').trim();
  const logoHref = httpUrl(logoText);
  const logoMatch = compareLogoDomain(logoText, c.onboarding?.data?.website);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>KYC Checklist — Know Your Customer</h3>
          <div style={{ fontSize: 12, color: C.textMuted }}>Click any item to expand evidence log · Each verification must include source, search terms, and findings</div>
        </div>
        <div style={{ fontSize: 12 }}>
          {/* #772 — an item counts when it is ticked AND meets its evidence
              minimum (a tick left over from before the minimums does not),
              the same rule as the Audit Readiness Report. A grandfathered
              case counts ticks, as it always did. */}
          {(() => { const total = Object.keys(KYC_ITEMS).filter(k => KYC_ITEMS[k].required).length; const done = Object.keys(KYC_ITEMS).filter(k => KYC_ITEMS[k].required && c.kyc[k]?.verified && (!kycMinimumsApply(c) || kycItemProgress(KYC_ITEMS[k], c.kyc[k], c.financialRefs).met)).length; return <span style={{ color: done === total ? C.success : C.warn, fontWeight: 600 }}>{done}/{total} required verified</span>; })()}
        </div>
      </div>
      {hasOnboardingData(c.onboarding?.data) && (
        <div
          data-testid="kyc-onboarding-card"
          style={{
            ...styles.card,
            // The gold is a TINT — rgba at 8% light, 14% dark — so on its own
            // it is see-through. In flow that was invisible; pinned, the
            // checklist cards slide underneath and their text composited
            // through it. zIndex orders the painting, it does not make a fill
            // opaque. So the tint is painted as a one-colour gradient layer
            // OVER the card surface the rest of the portal uses, which is
            // opaque in both palettes (#FFFFFF / #181d23).
            background: `linear-gradient(${C.goldBg}, ${C.goldBg}), ${C.bgCard}`,
            borderColor: C.gold,
            marginBottom: 16,
            // #696 — pinned so the declared identity stays on screen while the
            // analyst works the checklist below it. Verifying "does the
            // Secretary of State record match the declared legal name and
            // address" was a scroll to the top and back for every item.
            //
            // The scroll port is the shell's <main> (styles.main: overflowY
            // auto, maxHeight 100vh), and sticky resolves against the nearest
            // scrolling ancestor, so this works from here — no change to
            // CaseDetail/index.tsx or the shell. Nothing above the card is
            // fixed or sticky: the case header and the tab strip live INSIDE
            // that same scroll port and scroll away with everything else, so
            // the offset that puts the card flush against the top of the
            // visible area is 0, not a header height. If a fixed header is
            // ever added above the tab panel, this is the number that changes.
            position: 'sticky',
            top: 0,
            // Above the checklist rows it overlaps, below the toast (1000).
            zIndex: 2,
          }}
        >
          <h4 style={{ fontSize: 13, fontWeight: 600, color: C.goldText, marginBottom: cardCollapsed ? 0 : 8 }}>
            {/* The whole title row is the toggle. A native <button> is what
                makes it keyboard operable — Enter and Space activate it and it
                is in the tab order without a tabindex — so there is no
                hand-rolled onKeyDown here to double-fire against it. */}
            <button
              type="button"
              aria-expanded={!cardCollapsed}
              aria-controls={ONBOARDING_BODY_ID}
              onClick={() => setCardCollapsed(!cardCollapsed)}
              style={{ ...styles.btnUnstyled, display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}
            >
              <span aria-hidden="true" style={{ fontSize: 12, lineHeight: 1, transform: cardCollapsed ? 'none' : 'rotate(90deg)', transition: 'transform 0.2s' }}>▸</span>
              {/* Who filed this, best available. `submittedBy` is stamped by
                  Submit Onboarding and is the true answer when it exists;
                  #693 replaced the `oaName` fallback with the submission's
                  authorizer, since 382 is the Onboarding Agent on every case
                  and the old fallback named 382 rather than whoever filed.
                  A case with neither drops the parenthetical rather than
                  rendering it blank. */}
              Onboarding Data{(c.onboarding?.submittedBy || c.onboarding?.data?.authorizerName) && ` (submitted by ${c.onboarding?.submittedBy || c.onboarding?.data?.authorizerName})`}
              {/* The signal the old status guard was reaching for, said instead
                  of enacted: the analyst sees the declared identity AND that it
                  is not yet formally submitted. */}
              {c.onboarding?.status === 'DRAFT' && (
                <span style={{ ...styles.badge, marginLeft: 8, background: C.bgInput, color: C.textMuted, fontSize: 10 }}>
                  NOT YET SUBMITTED
                </span>
              )}
            </button>
          </h4>
          {cardCollapsed ? (
            // Collapsed: the four fields an analyst cross-checks most, on one
            // line. Truncated with an ellipsis rather than wrapped, because a
            // summary that can grow to two lines defeats the point of pinning
            // it; the full text is on the title attribute for the long ones.
            <div
              id={ONBOARDING_BODY_ID}
              data-testid="kyc-onboarding-summary"
              title={summaryLine(c.onboarding?.data)}
              style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {summaryLine(c.onboarding?.data)}
            </div>
          ) : (
            <div id={ONBOARDING_BODY_ID} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 12 }}>
              <div><span style={{ color: C.textMuted }}>Legal Name:</span> {c.onboarding?.data?.legalName}</div>
              <div><span style={{ color: C.textMuted }}>FEIN:</span> {c.onboarding?.data?.fein}</div>
              <div><span style={{ color: C.textMuted }}>Website:</span> {c.onboarding?.data?.website}</div>
              <div><span style={{ color: C.textMuted }}>Org Type:</span> {formatOrgType(c.onboarding?.data?.orgType)}</div>
              <div><span style={{ color: C.textMuted }}>Jurisdiction:</span> {c.onboarding?.data?.jurisdiction}</div>
              <div><span style={{ color: C.textMuted }}>SIC:</span> {c.onboarding?.data?.sicCode} — {c.onboarding?.data?.sicDescription}</div>
              <div><span style={{ color: C.textMuted }}>Authorizer:</span> {c.onboarding?.data?.authorizerName} ({c.onboarding?.data?.authorizerEmail})</div>
              <div><span style={{ color: C.textMuted }}>Address:</span> {c.onboarding?.data?.address}, {c.onboarding?.data?.city}, {c.onboarding?.data?.state} {c.onboarding?.data?.zip}</div>
              {/* Full width: a logo URL is long, and the hint sits after it.
                  Like the RCD/SHAFT link-out, opening it fetches from the
                  registrant's server — the hint is there so the analyst
                  usually does not need to. */}
              <div data-testid="kyc-logo-row" style={{ gridColumn: '1 / -1' }}>
                <span style={{ color: C.textMuted }}>Call Logo:</span>{' '}
                {logoHref ? (
                  <a href={logoHref.href} target="_blank" rel="noopener noreferrer" style={{ color: C.goldText, wordBreak: 'break-all' }}>{logoText} ↗</a>
                ) : (
                  <span style={{ wordBreak: 'break-all' }}>{logoText}</span>
                )}
                {logoText !== '' && (
                  <span data-testid="kyc-logo-domain-hint" style={{ marginLeft: 8, color: C.textMuted }}>
                    {logoMatch.kind === 'same'
                      ? 'Logo host matches website domain'
                      : logoMatch.kind === 'different'
                        ? `Logo hosted on a different domain (${logoMatch.host})`
                        : 'Could not compare logo host with website domain'}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {Object.entries(KYC_ITEMS).map(([key, item]) => {
        const kycField = c.kyc[key] || { verified: false, evidence: [] };
        const isExpanded = kycExpanded === key;
        const hasEvidence = kycField.evidence?.length > 0;
        const hasFlagged = kycField.evidence?.some((e: any) => e.result === 'flagged');
        // #772 — the evidence minimum. On a grandfathered case `minimums` is
        // false and the box behaves as it always did.
        const minimums = kycMinimumsApply(c);
        const progress = kycItemProgress(item, kycField, c.financialRefs);
        const shortfall = minimums ? shortfallReason(item, progress) : null;
        return (
          <div key={key} style={{ ...styles.card, marginBottom: 8, padding: 0, overflow: 'hidden', borderColor: hasFlagged ? C.danger : kycField.verified ? C.success : C.border }}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 10, background: isExpanded ? C.bgInput : 'transparent' }}>
              {/* #772 — below the minimum the box cannot be TICKED; it can
                  always be unticked, so a box left ticked by older data is not
                  stuck. The reason is the box's description. */}
              <input
                type="checkbox"
                aria-label={`${item.label} verified`}
                aria-describedby={shortfall ? `kyc-shortfall-${key}` : undefined}
                checked={kycField.verified || false}
                disabled={!!shortfall && !kycField.verified}
                onChange={e => {
                  // `disabled` is the control; this is the rule, for any
                  // path that reaches the handler anyway.
                  if (e.target.checked && shortfall) return;
                  const newKyc = { ...c.kyc, [key]: { ...kycField, verified: e.target.checked } };
                  updateCase(c.id, {
                    kyc: newKyc,
                    action: { code: 'KYC_UPDATED', detail: { item: key, label: item.label, verified: e.target.checked } },
                  });
                }}
              />
              <button type="button" aria-expanded={isExpanded} style={{ ...styles.btnUnstyled, flex: 1 }} onClick={() => setKycExpanded(isExpanded ? null : key)}>
                <span style={{ fontWeight: 600, color: kycField.verified ? C.success : hasFlagged ? C.danger : C.text, fontSize: 13 }}>
                  {item.label}
                </span>
                {item.required && <span style={{ fontSize: 10, color: C.danger, marginLeft: 6 }}>REQUIRED</span>}
                {hasEvidence && (
                  <span style={{ ...styles.badge, marginLeft: 8, background: hasFlagged ? C.dangerBg : C.successBg, color: hasFlagged ? C.danger : C.success, fontSize: 10 }}>
                    {kycField.evidence.length} record{kycField.evidence.length > 1 ? 's' : ''}
                    {hasFlagged && ' · FLAGGED'}
                  </span>
                )}
                {minimums && (
                  <span data-testid={`kyc-progress-${key}`} style={{ display: 'block', fontSize: 11, marginTop: 2, color: progress.met ? C.success : C.textMuted }}>
                    {progressText(item, progress)}
                    {shortfall && <span id={`kyc-shortfall-${key}`} style={{ color: C.warn }}> — {shortfall}</span>}
                  </span>
                )}
              </button>
              <div style={{ display: 'flex', gap: 6 }}>
                {item.sources.filter(s => s.url).map((s, i) => (
                  <a key={i} href={s.url!} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: C.goldText, textDecoration: 'none' }}>{s.name} ↗</a>
                ))}
              </div>
              <span aria-hidden="true" style={{ color: C.textDim, fontSize: 16, transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>▸</span>
            </div>
            {isExpanded && (
              <div style={{ borderTop: `1px solid ${C.border}`, padding: 16, background: C.bgInput }}>
                <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>
                  <strong>Data Sources:</strong> {item.sources.map(s => s.name).join(' · ')}
                </div>
                {/* #695 — the financial references, inside the item they are
                    evidence for. They used to be a tab of their own, and an
                    analyst verifying them over there had no way to see that
                    the required checklist item over here still read unverified.
                    Above the Evidence Log because they are the source material:
                    you call the references, then you write up what they said. */}
                {key === 'financial' && <FinancialReferencesPanel c={c} />}
                {kycField.evidence?.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: C.textSecondary }}>Evidence Log</div>
                    {kycField.evidence.map((ev: any, i: number) => (
                      <div key={ev.id || i} style={{ background: C.white, border: `1px solid ${ev.result === 'flagged' ? C.danger : C.border}`, borderRadius: 8, padding: 12, marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ ...styles.tag, background: ev.result === 'clear' ? C.successBg : ev.result === 'flagged' ? C.dangerBg : C.warnBg, color: ev.result === 'clear' ? C.success : ev.result === 'flagged' ? C.danger : C.warn, fontWeight: 600 }}>
                              {ev.result === 'clear' ? '✓ CLEAR' : ev.result === 'flagged' ? '⚠ FLAGGED' : '? INCONCLUSIVE'}
                            </span>
                            <span style={{ ...styles.tag, background: C.infoBg, color: C.info }}>{ev.source}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, color: C.textDim }}>{fmtDateTime(ev.date)} · {verifiedBy(ev.verifiedBy)}</span>
                            {/* #719 — every staff role that can WRITE the case.
                                #690 made this admins-only; product review
                                reversed that, because analysts and approvers
                                are the ones who file evidence and the ones who
                                spot a record on the wrong case. Legal is the
                                exception, and not by policy: `legal_isolation`
                                in 9999_security.sql is FOR SELECT only, so a
                                legal PATCH matches no row and the route answers
                                404/409 (measured in cases-full-shape). Offering
                                a Delete that cannot land is worse here than
                                anywhere else on the tab — the dialog says the
                                record is about to go. */}
                            {!user.isLegal && (
                              <button
                                type="button"
                                aria-label={`Delete evidence record from ${ev.source}`}
                                style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall, color: C.danger, padding: '2px 8px', fontSize: 11 }}
                                onClick={() => setConfirmRemove({ key, id: ev.id, index: i })}
                              >Delete</button>
                            )}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, marginBottom: 4 }}><span style={{ color: C.textMuted }}>Search:</span> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{ev.searchTerms}</span></div>
                        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{ev.findings}</div>
                        {/* #774 — a record with no id (it predates genId('EVD'))
                            cannot be named by a file, so it offers none. */}
                        {ev.id && (
                          <EvidenceAttachments
                            caseId={c.id}
                            target={{ section: 'kyc', kycItem: key, evidenceId: ev.id }}
                            files={attached.filter((f) => attachedTo(f, { section: 'kyc', kycItem: key, evidenceId: ev.id }))}
                            onChanged={refreshAttached}
                            queued={queued[ev.id]}
                            onQueuedTaken={() => takeQueued(ev.id)}
                            onQueuedLost={(names) => showToast(
                              `${names.length === 1 ? 'A file' : `${names.length} files`} could not be attached because the evidence record was not found in time: ${names.join(', ')}. Check whether the record was saved; if it was, attach ${names.length === 1 ? 'it' : 'them'} to it, and add the record again only if it is missing.`,
                            )}
                            readOnly={user.isLegal}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* #718 — ON FINANCIAL STABILITY THIS FORM SITS BESIDE THE
                    REFERENCE PANEL, and the two cover different sources. A
                    reference call is recorded once, through `+ Add Reference`,
                    which now derives its evidence record from the reference
                    (AddFinancialRefModal) — that is what stopped the same call
                    being typed into both forms. But three of the item's five
                    sources are not references at all (constants.ts: DUNS / D&B,
                    FEIN Verification, Audited Financial Statements — the other
                    two ARE the business and banking references, which
                    + Add Reference covers), and this is the only way evidence
                    for those three reaches the log. Removing it left an analyst
                    reviewing audited statements ticking the box by hand against
                    an empty Evidence Log, which is the #718 symptom on another
                    path. So it renders on every item, this one included. */}
                <div ref={addFormRef} style={{ background: C.white, border: `2px dashed ${C.border}`, borderRadius: 8, padding: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 12, color: C.goldText }}>+ Add Evidence Record</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <div>
                      <label htmlFor={`kyc-source-${key}`} style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>Source</label>
                      <select id={`kyc-source-${key}`} style={{ ...styles.select, width: '100%', fontSize: 12, padding: '8px 10px' }} value={evSource || item.sources[0]?.name || 'Other'} onChange={e => setEvSource(e.target.value)}>
                        {item.sources.map((s, i) => <option key={i} value={s.name}>{s.name}</option>)}
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor={`kyc-result-${key}`} style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>Result</label>
                      <select id={`kyc-result-${key}`} style={{ ...styles.select, width: '100%', fontSize: 12, padding: '8px 10px' }} value={evResult} onChange={e => setEvResult(e.target.value)}>
                        <option value="clear">✓ Clear</option>
                        <option value="flagged">⚠ Flagged</option>
                        <option value="inconclusive">? Inconclusive</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <label htmlFor={`kyc-search-${key}`} style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>Search Terms Used</label>
                    <input id={`kyc-search-${key}`} style={{ ...styles.input, fontSize: 12, padding: '8px 10px' }} placeholder='e.g. "Acme Corp" entity search, Delaware SOS' value={evSearch} onChange={e => setEvSearch(e.target.value)} />
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <label htmlFor={`kyc-findings-${key}`} style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>Findings / Evidence Summary *</label>
                    <textarea
                      id={`kyc-findings-${key}`}
                      style={{ ...styles.textarea, fontSize: 12, minHeight: 60, ...(evError ? { borderColor: C.danger } : {}) }}
                      placeholder="What did you find? Paste relevant search results, note case numbers, or describe the verification outcome..."
                      value={evFindings}
                      aria-invalid={evError ? true : undefined}
                      aria-describedby={evError ? `kyc-findings-error-${key}` : undefined}
                      onChange={e => { setEvFindings(e.target.value); if (evError) setEvError(null); }}
                    />
                    {evError && <div id={`kyc-findings-error-${key}`} role="alert" style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>{evError}</div>}
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <EvidenceAttachments
                      caseId={c.id}
                      label="Screenshots and PDFs (uploaded when the record is saved)"
                      staged={evStaged}
                      onStagedChange={setEvStaged}
                      pasteTarget={addFormRef}
                    />
                  </div>
                  <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => {
                    if (!evFindings.trim()) { setEvError('Findings are required — describe the verification outcome before saving.'); return; }
                    const source = evSource || item.sources[0]?.name || 'Other';
                    const newEvidence = { id: genId('EVD'), date: new Date().toISOString(), source, searchTerms: evSearch, result: evResult, findings: evFindings, verifiedBy: user.id };
                    // #689 — a CLEAR record completes the section, so the save
                    // carries the verification with it: ONE PATCH, one audit
                    // row, and the card is green when the analyst lets go of
                    // the button. Only 'clear' does this. 'flagged' and
                    // 'inconclusive' both record an INCOMPLETE verification, so
                    // they leave `verified` exactly as the analyst had it — the
                    // manual checkbox still governs, in both directions. The
                    // `?? false` normalises a field predating createEmptyKyc
                    // rather than writing `undefined` into the audit detail.
                    //
                    // #772 — and only once the item meets its evidence minimum
                    // with this record in it: the first of the two Legal
                    // records, or a second from the same source, leaves the box
                    // as it was. A grandfathered case keeps the old rule.
                    const evidence = [...(kycField.evidence || []), newEvidence];
                    const meetsMinimum = !minimums || kycItemProgress(item, { evidence }, c.financialRefs).met;
                    const verified = evResult === 'clear' && meetsMinimum ? true : (kycField.verified ?? false);
                    const updatedField = { ...kycField, evidence, verified };
                    const newKyc = { ...c.kyc, [key]: updatedField };
                    updateCase(c.id, {
                      kyc: newKyc,
                      action: { code: 'KYC_EVIDENCE_ADDED', detail: { item: key, label: item.label, source, result: evResult, verified } },
                    });
                    if (evStaged.length > 0) setQueued((q) => ({ ...q, [newEvidence.id]: evStaged }));
                    setEvSearch(''); setEvFindings(''); setEvError(null); setEvStaged([]);
                  }}>Save Evidence Record</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {/* #690 — the confirmation. A record can carry an approver's reason for
          a decision, so the dialog shows WHICH record is about to go rather
          than asking about "this record" in the abstract. Deleting is not
          undoable from the UI; the audit row is what survives it. */}
      {confirmRemove && (() => {
        const field = c.kyc[confirmRemove.key] || { verified: false, evidence: [] };
        const list: any[] = field.evidence || [];
        const i = confirmRemove.id ? list.findIndex((e: any) => e.id === confirmRemove.id) : confirmRemove.index;
        const ev = i >= 0 ? list[i] : undefined;
        if (!ev) return null;
        // #772 — whether this deletion unchecks the item, by the same rule
        // `removeEvidence` applies.
        const remaining = list.filter((_: any, j: number) => j !== i);
        const unchecks = !!field.verified && (kycMinimumsApply(c)
          ? !kycItemProgress(KYC_ITEMS[confirmRemove.key], { evidence: remaining }, c.financialRefs).met
          : remaining.length === 0);
        return (
          <Dialog title="Delete evidence record?" titleStyle={{ color: C.danger }} onClose={() => setConfirmRemove(null)}>
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              This removes the record from <strong>{KYC_ITEMS[confirmRemove.key]?.label ?? confirmRemove.key}</strong> on
              this case. It cannot be undone from the portal — the audit trail keeps a copy of what the record said.
            </div>
            <div style={{ background: C.bgInput, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12 }}>
              <div style={{ marginBottom: 4 }}><span style={{ color: C.textMuted }}>Source:</span> {ev.source} · <span style={{ color: C.textMuted }}>Result:</span> {ev.result}</div>
              <div style={{ marginBottom: 4 }}><span style={{ color: C.textMuted }}>Recorded:</span> {fmtDateTime(ev.date)} by {verifiedBy(ev.verifiedBy)}</div>
              <div style={{ color: C.text, lineHeight: 1.5 }}>{ev.findings}</div>
            </div>
            {unchecks && (
              <div style={{ fontSize: 12, color: C.warn, marginBottom: 16 }}>
                {remaining.length === 0
                  ? 'This is the only evidence on the item, so it will also be marked unverified.'
                  : 'Without this record the item is below its evidence minimum, so it will also be marked unverified.'}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={{ ...styles.btn, ...styles.btnDanger }}
                onClick={() => removeEvidence(confirmRemove.key, confirmRemove)}
              >Delete record</button>
              <button style={{ ...styles.btn, ...styles.btnOutline }} onClick={() => setConfirmRemove(null)}>Cancel</button>
            </div>
          </Dialog>
        );
      })()}
    </div>
  );
}
